import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { imgSrc } from "@/lib/img";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Mic, Newspaper, MessageCircle, ArrowRight, Quote, Video, Star } from "lucide-react";

export const TYPE_META = {
  interview: { icon: MessageCircle, label: "Interview" },
  podcast: { icon: Mic, label: "Podcast" },
  article: { icon: Newspaper, label: "Article" },
  video: { icon: Video, label: "Video" },
};

function ContentCard({ c, delay = 0 }) {
  const meta = TYPE_META[c.content_type] || TYPE_META.article;
  return (
    <Reveal delay={delay}>
      <Link to={`/community/${c.id}`} data-testid={`community-card-${c.id}`}
        className="group block bg-white border border-forest/10 rounded-3xl overflow-hidden h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-forest/5">
        <div className="h-48 bg-forest/10 overflow-hidden relative">
          {c.cover_image ? (
            <img src={imgSrc(c.cover_image)} alt={c.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-forest to-forest/70" />
          )}
          {c.featured && (
            <Badge className="absolute top-3 left-3 rounded-full text-[10px] uppercase tracking-wide bg-terracotta text-white border-none">
              <Star className="w-3 h-3 mr-1" /> Featured
            </Badge>
          )}
        </div>
        <div className="p-6">
          <Badge className="rounded-full text-[10px] uppercase tracking-wide bg-terracotta/10 text-terracotta border-none">
            <meta.icon className="w-3 h-3 mr-1" />{meta.label}
          </Badge>
          <div className="font-display text-xl text-forest mt-3 leading-snug">{c.title}</div>
          <p className="text-sm text-neutral-600 mt-2 line-clamp-2">{c.body}</p>
          <div className="text-xs text-neutral-500 mt-4">{c.author_name} · {new Date(c.created_at).toLocaleDateString()}</div>
        </div>
      </Link>
    </Reveal>
  );
}

function Section({ testId, kicker, title, items }) {
  if (items.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 py-8" data-testid={testId}>
      <Reveal>
        <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">{kicker}</div>
        <h2 className="font-display text-3xl sm:text-4xl text-forest mt-3 leading-tight tracking-tight">{title}</h2>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {items.map((c, i) => <ContentCard key={c.id} c={c} delay={(i % 3) * 0.08} />)}
      </div>
    </section>
  );
}

export default function Community() {
  const [content, setContent] = useState([]);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    api.contentList().then(setContent).catch(() => {});
    api.stories(3).then(setStories).catch(() => {});
  }, []);

  const featured = content.filter(c => c.featured);
  const written = content.filter(c => !c.featured && ["article", "interview"].includes(c.content_type));
  const av = content.filter(c => !c.featured && ["podcast", "video"].includes(c.content_type));

  return (
    <div className="bg-cream" data-testid="community-page">
      <Seo title="Blog & News" description="Interviews with senior volunteers, podcast episodes, articles and real stories — the beating heart of CivicHearth." />

      <section className="max-w-7xl mx-auto px-6 pt-16 pb-6">
        <div className="animate-fade-up max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">The people behind the hours</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-forest mt-4 leading-[1.05]">
            Blog &amp;<br /><span className="shine-text">News.</span>
          </h1>
          <p className="text-forest/70 mt-5 text-lg max-w-xl">Interviews with senior volunteers, podcast episodes and articles — plus stories straight from your neighbours.</p>
        </div>
      </section>

      {content.length === 0 && (
        <div className="max-w-7xl mx-auto px-6 py-16 text-center text-neutral-500 text-sm">No content published yet — check back soon.</div>
      )}

      <Section testId="community-featured-section" kicker="Editor's picks" title="Featured Content" items={featured} />
      <Section testId="community-written-section" kicker="Read" title="Blogs, Articles & Interviews" items={written} />
      <Section testId="community-av-section" kicker="Watch & listen" title="Podcasts & Videos" items={av} />

      {/* Voices from the field */}
      {stories.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-14" data-testid="community-voices-section">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">Community stories</div>
                <h2 className="font-display text-3xl sm:text-4xl text-forest mt-3 leading-tight tracking-tight">From your neighbours</h2>
              </div>
              <Link to="/stories" className="text-sm font-medium text-forest hover:text-forest-hover inline-flex items-center gap-1.5">
                Read & share stories <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-8">
            {stories.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.08}>
                <Link to="/stories" className="block bg-white border border-forest/10 rounded-2xl p-6 h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
                  <Quote className="w-5 h-5 text-terracotta/60" />
                  <p className="text-sm text-neutral-700 leading-relaxed line-clamp-3 mt-3">"{s.text}"</p>
                  <div className="text-xs text-neutral-500 mt-4">
                    {s.user_name}{s.hours ? ` · ${s.hours}h with ${s.org_name}` : s.story_type === "gathering" ? " · gathering recap" : s.story_type === "neighbour_help" ? " · neighbour help" : ""}
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-forest text-white py-16 mt-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl leading-tight tracking-tight">Got a story worth telling?</h2>
            <p className="text-white/80 mt-3 max-w-lg mx-auto">Share your volunteering story with the community — or nominate a senior volunteer we should interview.</p>
            <Link to="/stories"><Button className="rounded-full bg-terracotta hover:bg-terracotta-hover px-7 h-12 mt-7">Share your story</Button></Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
